/**
 * Runtime Cognito / Amplify auth settings from Create React App env vars.
 *
 * REACT_APP_COGNITO_CLIENT_ID — optional; overrides aws-exports userPoolClientId.
 *   If unset (e.g. no .env.local), userPoolClientId is taken only from src/aws-exports.js
 *   (default in this repo: 2jfboguf872voqsvks2gbl9sug).
 *
 * REACT_APP_COGNITO_USER_POOL_ID — optional; overrides aws-exports userPoolId.
 *   Set this whenever REACT_APP_COGNITO_CLIENT_ID points at an app client in a *different*
 *   user pool than aws-exports. Pool id and client id must be a matching pair from Cognito.
 *
 * Password login (USER_PASSWORD_AUTH): Cognito does not expose app-client auth
 * flows to the browser, so enable password UI when you know the app client allows
 * USER_PASSWORD_AUTH:
 *   REACT_APP_COGNITO_USER_PASSWORD_AUTH=true
 * or:
 *   REACT_APP_COGNITO_AUTH_FLOW=USER_PASSWORD_AUTH
 *
 * Explicit sign-in mode (overrides the implicit password-only detection above):
 *   REACT_APP_COGNITO_AUTH_MODE=email    — email OTP only (USER_AUTH)
 *   REACT_APP_COGNITO_AUTH_MODE=password — username/password only
 *   REACT_APP_COGNITO_AUTH_MODE=both      — show both options locally (default Amplify flow USER_AUTH;
 *     password sign-in still sends USER_PASSWORD_AUTH per request). Your app client must allow both flows.
 */

function isTruthyEnv(value) {
  if (value == null || value === '') return false;
  const v = String(value).trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

/**
 * @returns {boolean} Whether to show username/password sign-in and use USER_PASSWORD_AUTH.
 */
export function isUserPasswordAuthMode() {
  if (isTruthyEnv(process.env.REACT_APP_COGNITO_USER_PASSWORD_AUTH)) {
    return true;
  }
  const flow = process.env.REACT_APP_COGNITO_AUTH_FLOW;
  if (!flow) return false;
  return String(flow).trim().toUpperCase() === 'USER_PASSWORD_AUTH';
}

/**
 * @returns {'email' | 'password' | 'both'}
 */
export function getCognitoAuthMode() {
  const raw = process.env.REACT_APP_COGNITO_AUTH_MODE;
  if (raw != null && String(raw).trim() !== '') {
    const m = String(raw).trim().toLowerCase();
    if (m === 'email' || m === 'password' || m === 'both') {
      return m;
    }
  }
  return isUserPasswordAuthMode() ? 'password' : 'email';
}

/**
 * @param {object} awsmobile — base Amplify config from aws-exports
 * @returns {object} Config object for Amplify.configure
 */
export function buildAmplifyAuthConfig(awsmobile) {
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID?.trim();
  const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID?.trim();
  const mode = getCognitoAuthMode();
  const usePasswordAuth = mode === 'password';
  const baseCognito = awsmobile.Auth?.Cognito ?? {};

  return {
    ...awsmobile,
    Auth: {
      ...awsmobile.Auth,
      Cognito: {
        ...baseCognito,
        ...(userPoolId ? { userPoolId } : {}),
        ...(clientId ? { userPoolClientId: clientId } : {}),
        authenticationFlowType: usePasswordAuth
          ? 'USER_PASSWORD_AUTH'
          : (baseCognito.authenticationFlowType ?? 'USER_AUTH'),
      },
    },
  };
}
